import React, { useState } from 'react';
import { Button, Card, Typography, message } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/auth';

const { Title, Text } = Typography;

const AuthTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated, login, logout } = useAuth();

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      // This will use the Google JavaScript API directly
      // @ts-ignore - Google API loaded via script tag
      if (!window.google) {
        message.error('Google API not loaded. Please refresh the page.');
        return;
      }

      // @ts-ignore
      const response = await window.google.accounts.id.prompt();
      
      if (response.credential) {
        // Send the ID token to our backend for verification
        const { token } = await authApi.verifyGoogleToken(response.credential);
        login(token);
        message.success('Successfully authenticated!');
      }
    } catch (error) {
      console.error('Authentication failed:', error);
      message.error('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackendRedirect = async () => {
    setLoading(true);
    try {
      const { authUrl } = await authApi.getGoogleAuthUrl();
      // Open in same tab to avoid popup blocking
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      message.error('Failed to initialize authentication');
      setLoading(false);
    }
  };

  const testManualToken = async () => {
    // This is a test function to manually test token handling
    const testToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.test";
    try {
      login(testToken);
      message.success('Manual token test successful');
    } catch (error) {
      message.error('Manual token test failed');
    }
  };

  if (isAuthenticated && user) {
    return (
      <div style={{ maxWidth: 400, margin: '50px auto', padding: '20px' }}>
        <Card>
          <Title level={3}>Authentication Success! ✅</Title>
          <div style={{ marginBottom: 16 }}>
            <Text strong>User ID:</Text> {user.id}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Email:</Text> {user.email}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Name:</Text> {user.firstName} {user.lastName}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Role:</Text> {user.role}
          </div>
          <Button type="primary" danger onClick={logout} block>
            Logout
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: '50px auto', padding: '20px' }}>
      <Card>
        <Title level={3}>Authentication Test</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Test different authentication methods:
        </Text>
        
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<GoogleOutlined />}
            loading={loading}
            onClick={handleBackendRedirect}
            block
            size="large"
          >
            Login via Backend Redirect (OAuth Flow)
          </Button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Button
            type="default"
            icon={<GoogleOutlined />}
            loading={loading}
            onClick={handleGoogleLogin}
            block
            size="large"
            disabled
          >
            Direct Google Auth (Not Ready)
          </Button>
        </div>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          Note: Make sure you've configured the correct redirect URIs in Google Cloud Console:
          <br />
          • http://localhost:3000/api/v1/auth/google/callback
          <br />
          • http://localhost:5174/auth/callback
        </Text>
      </Card>
    </div>
  );
};

export default AuthTest;